/* Signup popup — Klaviyo Wave-2 acquisition (spec §7.3)
 *
 * Trigger: timed delay (default 12s) or desktop exit-intent, whichever first.
 * Frequency: show once → 30-day localStorage suppress; permanent after
 * subscribe. Holds (re-arms) while a mid-page quiz is in progress or the cart
 * drawer is open. Subscribes to the per-store MASTER list via Klaviyo's public
 * client API (same pattern as start-here-quiz.js), custom_source "Site Popup".
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'progurt-signup-popup';
  var SUPPRESS_DAYS = 30;
  var EXIT_INTENT_GRACE_MS = 3000;
  var RETRY_MS = 8000;
  var MAX_RETRIES = 3;

  var root = document.getElementById('SignupPopup');
  if (!root) return;

  var companyId = root.getAttribute('data-klaviyo-company');
  var listId = root.getAttribute('data-master-list');
  var delayMs = (parseInt(root.getAttribute('data-delay'), 10) || 12) * 1000;
  var exitIntent = root.getAttribute('data-exit-intent') === 'true';
  var designMode = window.Shopify && window.Shopify.designMode;

  var panel = root.querySelector('.signup-popup__panel');
  var form = root.querySelector('.signup-popup__form');
  var emailInput = root.querySelector('.signup-popup__input');
  var errorEl = root.querySelector('[data-popup-error]');
  var stepForm = root.querySelector('[data-popup-step="form"]');
  var stepSuccess = root.querySelector('[data-popup-step="success"]');

  var opened = false;
  var retries = 0;
  var loadedAt = Date.now();
  var lastFocused = null;

  function readState() {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; } catch (e) { return {}; }
  }
  function writeState(patch) {
    try {
      var s = readState();
      for (var k in patch) s[k] = patch[k];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(s));
    } catch (e) {}
  }

  function eligible() {
    var s = readState();
    if (s.subscribed) return false;
    if (s.shownAt && Date.now() - s.shownAt < SUPPRESS_DAYS * 864e5) return false;
    return true;
  }

  // Hold conditions checked at trigger time (re-arm, don't skip the visit).
  function blocked() {
    // Mid-page quiz in progress (advanced past its first step).
    var quizzes = document.querySelectorAll('[data-quiz-step]');
    for (var i = 0; i < quizzes.length; i++) {
      var step = quizzes[i].getAttribute('data-quiz-step');
      if (step && step !== 'q1') return true;
    }
    // Cart drawer open.
    var drawer = document.querySelector('cart-drawer');
    if (drawer && drawer.classList.contains('active')) return true;
    if (document.visibilityState === 'hidden') return true;
    return false;
  }

  function trigger() {
    if (opened || !eligible()) return;
    if (blocked()) {
      if (retries++ < MAX_RETRIES) setTimeout(trigger, RETRY_MS);
      return;
    }
    open();
  }

  function open(fromEditor) {
    if (opened) return;
    opened = true;
    lastFocused = document.activeElement;
    root.removeAttribute('hidden');
    void root.offsetWidth; // reflow so the fade transition runs
    root.classList.add('is-open');
    if (!fromEditor) writeState({ shownAt: Date.now() });
    document.addEventListener('keydown', onKeydown);
    // Focus the panel, not the email input (gift-led v2): the primary path is
    // the Monthly Protocol link and email capture is secondary — autofocusing
    // the input would skip the primary CTA and (on touch) pop the keyboard
    // over half the panel. Tab order still reaches the input naturally.
    if (!fromEditor) {
      setTimeout(function () { panel.focus(); }, 260);
    }
  }

  function close() {
    if (!opened) return;
    opened = true; // stays consumed for this pageview
    root.classList.remove('is-open');
    document.removeEventListener('keydown', onKeydown);
    setTimeout(function () { root.setAttribute('hidden', ''); }, 260);
    if (lastFocused && lastFocused.focus) lastFocused.focus();
  }

  function onKeydown(e) {
    if (e.key === 'Escape') { close(); return; }
    if (e.key !== 'Tab') return;
    // Light focus trap within the panel.
    var focusables = panel.querySelectorAll('button, input, [href]');
    var visible = [];
    for (var i = 0; i < focusables.length; i++) {
      if (focusables[i].offsetParent !== null) visible.push(focusables[i]);
    }
    if (!visible.length) return;
    var first = visible[0];
    var last = visible[visible.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  // Hardened 2026-07-07 (same pattern as start-here-quiz.js): third-party
  // window.fetch wrappers (Okendo loyalty) can reject/throw this call — fall
  // back to XMLHttpRequest, which they don't wrap, and surface failures to GA4.
  function subscribe(email) {
    var payload = JSON.stringify({
      data: {
        type: 'subscription',
        attributes: {
          custom_source: 'Site Popup',
          profile: { data: { type: 'profile', attributes: { email: email } } }
        },
        relationships: { list: { data: { type: 'list', id: listId } } }
      }
    });
    var url = 'https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId);
    return new Promise(function (resolve, reject) {
      function xhrFallback(why) {
        try {
          var x = new XMLHttpRequest();
          x.open('POST', url, true);
          x.setRequestHeader('revision', '2024-10-15');
          x.setRequestHeader('content-type', 'application/json');
          x.onload = function () {
            if (x.status >= 200 && x.status < 300) resolve();
            else reject(new Error(why + '+xhr_status_' + x.status));
          };
          x.onerror = function () { reject(new Error(why + '+xhr_network')); };
          x.send(payload);
        } catch (e) { reject(new Error(why + '+xhr_throw')); }
      }
      try {
        fetch(url, {
          method: 'POST',
          headers: { revision: '2024-10-15', 'content-type': 'application/json' },
          body: payload,
          keepalive: true
        }).then(function (res) {
          if (res.ok) resolve();
          else reject(new Error('fetch_status_' + res.status));
        }).catch(function () { xhrFallback('fetch_reject'); });
      } catch (e) { xhrFallback('fetch_throw'); }
    });
  }

  if (form) {
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      var email = (emailInput.value || '').trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        emailInput.classList.add('is-invalid');
        emailInput.focus();
        return;
      }
      emailInput.classList.remove('is-invalid');
      errorEl.setAttribute('hidden', '');
      var btn = form.querySelector('.signup-popup__cta');
      btn.setAttribute('disabled', '');
      subscribe(email).then(function () {
        writeState({ subscribed: true });
        stepForm.setAttribute('hidden', '');
        stepSuccess.removeAttribute('hidden');
        try {
          if (typeof window.gtag === 'function') {
            window.gtag('event', 'sign_up', { method: 'site_popup' });
          }
        } catch (err) {}
      }).catch(function (err) {
        btn.removeAttribute('disabled');
        errorEl.removeAttribute('hidden');
        try {
          if (typeof window.gtag === 'function') {
            window.gtag('event', 'popup_subscribe_error', { event_category: 'popup', error_detail: String(err && err.message || err) });
          }
        } catch (e2) {}
      });
    });
  }

  var dismissers = root.querySelectorAll('[data-popup-dismiss]');
  for (var d = 0; d < dismissers.length; d++) {
    dismissers[d].addEventListener('click', close);
  }

  // Theme-editor support: open on section select so RB can QA in the customizer.
  if (designMode) {
    document.addEventListener('shopify:section:select', function (e) {
      if (e.target && e.target.contains(root)) open(true);
    });
    document.addEventListener('shopify:section:deselect', function (e) {
      if (e.target && e.target.contains(root)) { opened = false; root.classList.remove('is-open'); root.setAttribute('hidden', ''); }
    });
    return; // no auto-triggers inside the editor
  }

  if (!eligible()) return;

  setTimeout(trigger, delayMs);

  if (exitIntent && window.matchMedia('(pointer: fine)').matches) {
    document.addEventListener('mouseout', function onExit(e) {
      if (Date.now() - loadedAt < EXIT_INTENT_GRACE_MS) return;
      if (e.relatedTarget || e.clientY > 0) return;
      document.removeEventListener('mouseout', onExit);
      trigger();
    });
  }
})();
